import React, { useState, useRef, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button, Spinner } from "reactstrap";

const FORCE_ASPECT = 1 / 1;

const ImageCropper = ({ imageFile, onCropComplete, aspect = FORCE_ASPECT }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!imageFile) return;

    let cancelled = false;

    const normalizeTo34Blur = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const ow = img.naturalWidth;
            const oh = img.naturalHeight;

            let cw = ow;
            let ch = Math.round(ow * aspect);
            if (ch < oh) {
              ch = oh;
              cw = Math.round(oh * (1 / aspect));
            }

            const canvas = document.createElement("canvas");
            canvas.width = cw;
            canvas.height = ch;
            const ctx = canvas.getContext("2d");
            if (!ctx) return reject("No context");

            const bgScale = Math.max(cw / ow, ch / oh);
            const bgW = ow * bgScale;
            const bgH = oh * bgScale;
            const bgX = (cw - bgW) / 2;
            const bgY = (ch - bgH) / 2;

            ctx.filter = "blur(20px) brightness(1)";
            ctx.drawImage(img, bgX, bgY, bgW, bgH);

            ctx.filter = "none";

            const fgScale = Math.min(1, cw / ow, ch / oh);
            const fgW = ow * fgScale;
            const fgH = oh * fgScale;
            const fgX = (cw - fgW) / 2;
            const fgY = (ch - fgH) / 2;

            ctx.drawImage(img, fgX, fgY, fgW, fgH);

            resolve(canvas.toDataURL("image/jpeg", 0.92));
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    };

    normalizeTo34Blur(imageFile).then((data) => {
      if (!cancelled) setSelectedImage(data);
    });

    return () => {
      cancelled = true;
    };
  }, [imageFile, aspect]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect, width, height),
      width,
      height
    );
    setCrop(initial);
  };

  const getCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const timestamp = new Date().getTime();
        const originalName = imageFile.name ? imageFile.name.split('.')[0] : 'image';
        const fileName = `${originalName}_cropped_${timestamp}.jpeg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });
        onCropComplete(file);
      }
    }, "image/jpeg");
  };

  return (
    <div style={{ textAlign: "center" }}>
      {selectedImage ? (
        <>
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            keepSelection
          >
            <img
              ref={imgRef}
              src={selectedImage}
              alt="Crop me"
              style={{ maxWidth: "100%", maxHeight: "450px", display: "block", margin: "0 auto" }}
              onLoad={onImageLoad}
            />
          </ReactCrop>

          <Button color="primary" className="mt-2" onClick={getCroppedImage}>
            Apply Crop
          </Button>
        </>
      ) : (
        <div>
          <Spinner color="primary" />
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
